import React, { useEffect, useState } from "react";
import { FaFlask, FaDownload, FaChevronDown, FaChevronRight, FaEdit, FaTrash } from "react-icons/fa";
import SpeciesEvidencePanel from "./SpeciesEvidencePanel";
import "./DepositorHistory.css";

const STORAGE_KEY = "dshb_entries";

// Simple in-memory cache so switching tabs doesn't re-fetch
const historyCache = {};

const renderVal = (value) => {
  if (!value || value === "" || value === 0) return "—";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
};

// Species reactivity evidence is an array of { species, type, fileName, linkUrl, linkLabel, confirmed }
// objects rather than plain strings, so it needs its own summary formatter.
// Proof is optional (only confirmation is mandatory - see
// SpeciesEvidenceSection.js), so a confirmed species with nothing
// attached is a real, valid state, not something to paper over with a
// misleading fallback like literally the word "file".
const renderEvidence = (evidence) => {
  if (!Array.isArray(evidence) || evidence.length === 0) return "—";
  return evidence.map(ev => {
    const hasProof = ev.type === "file" ? !!ev.fileName : !!(ev.linkUrl || "").trim();
    const proof = hasProof
      ? (ev.type === "file" ? ev.fileName : (ev.linkLabel || ev.linkUrl))
      : (ev.confirmed ? "confirmed, no proof attached" : "not confirmed");
    return `${ev.species}: ${proof}`;
  }).join("; ");
};

const renderColVal = (row, col) =>
  col.key === "speciesEvidence" ? renderEvidence(row[col.key]) : renderVal(row[col.key]);

// Full set of antibody fields — every field a depositor can fill in.
const columns = [
  { label: "Item Name/Number",        key: "item_name_number" },
  { label: "Isotype",                 key: "isotype" },
  { label: "Antigen",                 key: "antigen" },
  { label: "Alternative Antigen Names", key: "alternative_antigen_names" },
  { label: "Depositor Growth Medium", key: "depositor_growth_medium" },
  { label: "Institution",             key: "depositor_institution" },
  { label: "Depositor Email",         key: "depositor_email" },
  { label: "Depositor Website",       key: "depositor_website" },
  { label: "Depositor",               key: "depositor_name_contributor" },
  { label: "Host Species",            key: "host_species" },
  { label: "Antigen Species",         key: "antigen_species" },
  { label: "Positive Tested Species", key: "positive_tested_species" },
  { label: "Species Evidence",        key: "speciesEvidence" },
  { label: "Alternate Name/Clone ID", key: "alternate_name_clone_id" },
  { label: "Additional Information",  key: "additional_information" },
  { label: "Additional Characterization", key: "additional_characterization" },
  { label: "Depositor Notes",         key: "depositor_notes" },
  { label: "Collections",             key: "collections" },
  { label: "Antigen Mol. Weight",     key: "antigen_molecular_weight" },
  { label: "Gene",                    key: "gene" },
  { label: "Alternate Gene Names",    key: "alternate_gene_names" },
  { label: "Entrez Gene ID",          key: "entrez_gene_id" },
  { label: "UniProt ID",              key: "uniprot_id" },
  { label: "Immunogen",               key: "immunogen" },
  { label: "Immunogen Sequence",      key: "immunogen_sequence" },
  { label: "Clonality",               key: "clonality" },
  { label: "Myeloma Strain",          key: "myeloma_strain" },
  { label: "Epitope Mapped",          key: "epitope_mapped" },
  { label: "Epitope Location/Sequence", key: "epitope_location_sequence" },
  { label: "Protocol File",           key: "protocol_file_name" },
  { label: "Applications",            key: "applications" },
];

const statusColor = (status) => {
  const map = {
    "Pending":              { bg: "#fef9c3", color: "#854d0e" },
    "Approved":             { bg: "#dcfce7", color: "#15803d" },
    "Reviewed":             { bg: "#dbeafe", color: "#1e40af" },
    "Correction Requested": { bg: "#fee2e2", color: "#991b1b" },
  };
  return map[status] || { bg: "#f3f4f6", color: "#374151" };
};

export default function DepositorHistory({ userId, onEditEntry }) {
  const [activeSubTab, setActiveSubTab] = useState("submitted"); // "submitted" | "drafts"

  const [history, setHistory]          = useState([]);
  const [loading, setLoading]          = useState(true);
  const [expandedBatches, setExpanded] = useState({});
  const [expandedEvidenceRows, setExpandedEvidenceRows] = useState({});
  const toggleEvidenceRow = (submissionId) =>
    setExpandedEvidenceRows(prev => ({ ...prev, [submissionId]: !prev[submissionId] }));

  const [draftEntries, setDraftEntries] = useState([]);
  const [expandedDraftRows, setExpandedDraftRows] = useState({});

  // Read drafts from the single shared entries list — same key
  // DepositorForm uses, filtered to status === "draft".
  useEffect(() => {
    const readDrafts = () => {
      try {
        const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        setDraftEntries(Array.isArray(all) ? all.filter(e => e.status === "draft") : []);
      } catch { setDraftEntries([]); }
    };
    readDrafts();
    // Re-check on focus, in case a draft was added/edited on the Form tab.
    window.addEventListener("focus", readDrafts);
    return () => window.removeEventListener("focus", readDrafts);
  }, []);

  // Re-read drafts every time this tab becomes active (covers the case
  // where the user edits drafts on the Form tab, then comes back here
  // without the window losing focus, e.g. both are just tab switches
  // within the same SPA).
  useEffect(() => {
    try {
      const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      setDraftEntries(Array.isArray(all) ? all.filter(e => e.status === "draft") : []);
    } catch { setDraftEntries([]); }
  }, [activeSubTab]);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }

    if (historyCache[userId]) {
      setHistory(historyCache[userId]);
      setLoading(false);
      return;
    }

    const fetchHistory = async () => {
      try {
        const res = await fetch(
          `https://dshb-deposits.its.uiowa.edu/api/history?userId=${userId}`
        );
        if (!res.ok) { setHistory([]); setLoading(false); return; }
        const data = await res.json();
        const result = Array.isArray(data) ? data : (data.history || []);
        historyCache[userId] = result;
        setHistory(result);
      } catch (err) {
        console.error("Fetch failed:", err.message);
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [userId]);

  // ── Draft actions ────────────────────────────────────────────────
  const editDraft = (entry) => {
    // Pull this entry out of the shared list immediately — it now lives
    // in the Form as the entry being actively edited. It goes back in
    // (updated or not) when the user saves/updates/cancels there.
    try {
      const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const updated = all.filter(e => e.id !== entry.id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      setDraftEntries(updated.filter(e => e.status === "draft"));
    } catch { /* ignore */ }
    if (onEditEntry) onEditEntry(entry);
  };

  const deleteDraft = (id) => {
    try {
      const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const updated = all.filter(e => e.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      setDraftEntries(updated.filter(e => e.status === "draft"));
    } catch { /* ignore */ }
  };

  const toggleDraftRow = (id) =>
    setExpandedDraftRows(prev => ({ ...prev, [id]: !prev[id] }));

  // ── Submitted history (server data) ──────────────────────────────
  const batches = history.map((entry, idx) => ({
    batchId:  entry.id || idx,
    date:     entry.date  || "Unknown Date",
    time:     entry.time  || "",
    label:    entry.label || entry.date,
    products: entry.products || [entry],
  }));

  const toggleBatch = (id) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const exportCSV = (rows, filenamePrefix) => {
    const header = columns.map(c => c.label).join(",");
    const csvRows = rows.map(row =>
      columns.map(c => {
        let val = renderColVal(row, c);
        if (val.includes(",") || val.includes('"') || val.includes("\n"))
          val = `"${val.replace(/"/g, '""')}"`;
        return val;
      }).join(",")
    );
    const csv  = [header, ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `${filenamePrefix}_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const totalAntibodies = batches.reduce((sum, b) => sum + b.products.length, 0);

  // ── Tab bar (always shown, even while loading) ───────────────────
  const tabBar = (
    <div style={{
      display: "flex", gap: "4px", marginBottom: "20px",
      borderBottom: "2px solid #e2e8f0",
    }}>
      <button
        onClick={() => setActiveSubTab("submitted")}
        style={{
          padding: "10px 18px", background: "none", border: "none",
          borderBottom: activeSubTab === "submitted" ? "2px solid #1a2c5b" : "2px solid transparent",
          marginBottom: "-2px",
          color: activeSubTab === "submitted" ? "#1a2c5b" : "#6b7280",
          fontWeight: activeSubTab === "submitted" ? 700 : 500,
          fontSize: "14px", cursor: "pointer",
        }}
      >
        Submitted
      </button>
      <button
        onClick={() => setActiveSubTab("drafts")}
        style={{
          padding: "10px 18px", background: "none", border: "none",
          borderBottom: activeSubTab === "drafts" ? "2px solid #1a2c5b" : "2px solid transparent",
          marginBottom: "-2px",
          display: "flex", alignItems: "center", gap: "8px",
          color: activeSubTab === "drafts" ? "#1a2c5b" : "#6b7280",
          fontWeight: activeSubTab === "drafts" ? 700 : 500,
          fontSize: "14px", cursor: "pointer",
        }}
      >
        Drafts
        {draftEntries.length > 0 && (
          <span style={{
            background: activeSubTab === "drafts" ? "#1a2c5b" : "#e2e8f0",
            color: activeSubTab === "drafts" ? "#fff" : "#6b7280",
            borderRadius: "20px", padding: "1px 8px", fontSize: "11px", fontWeight: 700,
          }}>{draftEntries.length}</span>
        )}
      </button>
    </div>
  );

  // ── Drafts tab content ────────────────────────────────────────────
  const draftsContent = (
    <div>
      {draftEntries.length === 0 ? (
        <div className="history-empty">
          <FaFlask className="empty-icon" />
          <h3>No Drafts</h3>
          <p>Anything you save without submitting will show up here.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {draftEntries.map((row) => {
            const isExpanded = expandedDraftRows[row.id];
            return (
              <div key={row.id} style={{
                border: "1px solid #e2e8f0", borderRadius: "8px",
                overflow: "hidden", background: "#fff",
              }}>
                <div
                  onClick={() => toggleDraftRow(row.id)}
                  style={{
                    padding: "14px 18px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "12px",
                    userSelect: "none",
                    background: isExpanded ? "#f8fafc" : "#fff",
                    borderBottom: isExpanded ? "1px solid #e2e8f0" : "none",
                  }}
                >
                  <span style={{ color: "#6b7280", fontSize: "13px", flexShrink: 0 }}>
                    {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: "14px", color: "#1a2c5b" }}>
                      {row.item_name_number || "Untitled antibody"}
                    </span>
                    <span style={{
                      marginLeft: "10px",
                      background: "#fef9c3", color: "#854d0e",
                      borderRadius: "20px", padding: "2px 10px",
                      fontSize: "11px", fontWeight: 600,
                    }}>
                      Draft
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "6px", flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => editDraft(row)} style={{
                      display: "flex", alignItems: "center", gap: "5px",
                      background: "#1a2c5b", color: "#fff", border: "none", borderRadius: "5px",
                      padding: "6px 12px", cursor: "pointer", fontSize: "11px", fontWeight: 600,
                    }}>
                      <FaEdit style={{ fontSize: "10px" }} /> Edit
                    </button>
                    <button onClick={() => deleteDraft(row.id)} style={{
                      display: "flex", alignItems: "center", gap: "5px",
                      background: "none", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: "5px",
                      padding: "6px 12px", cursor: "pointer", fontSize: "11px", fontWeight: 600,
                    }}>
                      <FaTrash style={{ fontSize: "10px" }} /> Delete
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <tbody>
                        {columns.map(c => (
                          <tr key={c.key}>
                            <td style={{ ...td, fontWeight: 600, color: "#6b7280", width: "200px" }}>{c.label}</td>
                            <td style={td}>{renderColVal(row, c)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={() => exportCSV(draftEntries, "dshb_drafts")}
              style={{
                display: "flex", alignItems: "center", gap: "6px",
                background: "none", border: "1px solid #d1d5db", borderRadius: "6px",
                padding: "7px 14px", cursor: "pointer", fontSize: "12px", color: "#374151", fontWeight: 500,
              }}
            >
              <FaDownload style={{ fontSize: "11px" }} /> Export All Drafts CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ── Submitted tab content ─────────────────────────────────────────
  let submittedContent;
  if (loading) {
    submittedContent = (
      <div className="history-loading">
        <div className="spinner"></div>
        <p>Loading your submissions...</p>
      </div>
    );
  } else if (!history || history.length === 0) {
    submittedContent = (
      <div className="history-empty">
        <FaFlask className="empty-icon" />
        <h3>No Submissions Yet</h3>
        <p>You haven't submitted any antibodies yet.</p>
        <p>Start by depositing your antibody to contribute to the scientific community!</p>
      </div>
    );
  } else {
    submittedContent = (
      <div>
        <p className="submission-count" style={{ marginBottom: "16px" }}>
          {batches.length} submission{batches.length !== 1 ? "s" : ""}
          {" · "}
          {totalAntibodies} antibod{totalAntibodies !== 1 ? "ies" : "y"} total
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {batches.map((batch) => {
            const isExpanded = expandedBatches[batch.batchId];
            const statusCounts = batch.products.reduce((acc, p) => {
              const s = p.status || "Pending";
              acc[s] = (acc[s] || 0) + 1;
              return acc;
            }, {});
            const preview = batch.products
              .slice(0, 3)
              .map(p => p.item_name_number)
              .filter(Boolean)
              .join(", ");
            const moreCount = batch.products.length - 3;

            return (
              <div key={batch.batchId} style={{
                border: "1px solid #e2e8f0", borderRadius: "8px",
                overflow: "hidden", background: "#fff",
              }}>
                <div
                  onClick={() => toggleBatch(batch.batchId)}
                  style={{
                    padding: "14px 18px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "12px",
                    userSelect: "none",
                    background: isExpanded ? "#f8fafc" : "#fff",
                    borderBottom: isExpanded ? "1px solid #e2e8f0" : "none",
                  }}
                >
                  <span style={{ color: "#6b7280", fontSize: "13px", flexShrink: 0 }}>
                    {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                  </span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600, fontSize: "14px", color: "#1a2c5b" }}>
                        {batch.date}
                        {batch.time && (
                          <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: "6px", fontSize: "13px" }}>
                            at {batch.time}
                          </span>
                        )}
                      </span>
                      <span style={{
                        background: "#e0e7ff", color: "#3730a3",
                        borderRadius: "20px", padding: "2px 10px",
                        fontSize: "11px", fontWeight: 600, flexShrink: 0,
                      }}>
                        {batch.products.length} antibod{batch.products.length !== 1 ? "ies" : "y"}
                      </span>
                      {Object.entries(statusCounts).map(([s, count]) => {
                        const sc = statusColor(s);
                        return (
                          <span key={s} style={{
                            background: sc.bg, color: sc.color,
                            borderRadius: "20px", padding: "2px 10px",
                            fontSize: "11px", fontWeight: 600, flexShrink: 0,
                          }}>
                            {s}: {count}
                          </span>
                        );
                      })}
                    </div>
                    {!isExpanded && preview && (
                      <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#9ca3af",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {preview}{moreCount > 0 ? ` and ${moreCount} more…` : ""}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); exportCSV(batch.products, `dshb_submission_${batch.date.replace(/[^a-z0-9]/gi, "-")}`); }}
                    style={{
                      display: "flex", alignItems: "center", gap: "5px",
                      background: "none", border: "1px solid #d1d5db", borderRadius: "5px",
                      padding: "5px 10px", cursor: "pointer", fontSize: "11px",
                      color: "#374151", flexShrink: 0, fontWeight: 500,
                    }}
                  >
                    <FaDownload style={{ fontSize: "10px" }} /> Export CSV
                  </button>
                </div>

                {isExpanded && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <thead>
                        <tr>
                          <th style={th}>#</th>
                          {columns.map(c => <th key={c.key} style={th}>{c.label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {batch.products.map((row, idx) => {
                          const evidenceOpen = expandedEvidenceRows[row.submission_id];
                          return (
                          <React.Fragment key={idx}>
                            <tr style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                              <td style={td}>{idx + 1}</td>
                              {columns.map(c => (
                                <td key={c.key} style={td}>
                                  {c.key === "protocol_file_name" ? (
                                    row.protocol_file_name
                                      ? <a href={`https://dshb-deposits.its.uiowa.edu/api/download-protocol-file/${row.submission_id}`}
                                          target="_blank" rel="noreferrer" style={{ color: "#305DBD", fontWeight: 600 }}>
                                          {row.protocol_file_name}
                                        </a>
                                      : "—"
                                  ) : c.key === "speciesEvidence" ? (
                                    <button type="button" onClick={() => toggleEvidenceRow(row.submission_id)}
                                      style={{
                                        background: "none", border: "1px solid #d1d5db", borderRadius: "5px",
                                        padding: "3px 10px", cursor: "pointer", fontSize: "11px",
                                        color: "#305DBD", fontWeight: 600,
                                      }}>
                                      {evidenceOpen ? "Hide" : "View"} evidence
                                    </button>
                                  ) : renderVal(row[c.key])}
                                </td>
                              ))}
                            </tr>
                            {evidenceOpen && (
                              <tr>
                                <td colSpan={columns.length + 1} style={{ padding: "10px 16px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                  <SpeciesEvidencePanel submissionId={row.submission_id} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="history-container">
      <div className="history-header">
        <h2>History</h2>
      </div>

      {tabBar}

      {activeSubTab === "submitted" ? submittedContent : draftsContent}
    </div>
  );
}

const th = {
  padding: "8px 10px", textAlign: "left", fontWeight: 600,
  background: "#f8fafc", color: "#374151", fontSize: "11px",
  borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap",
};

const td = {
  padding: "8px 10px", borderBottom: "1px solid #f3f4f6",
  color: "#374151", maxWidth: "200px",
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};